import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { readStorage, removeStorage, writeStorageJson } from 'src/app/shared/utils/safe-storage';

export interface ListState {
  filter?: any;
  sort?: any[];
  pagination?: { page: number; size: number; length?: number };
  customData?: any;
}

@Injectable() // NOT root — provided per route/host component so each activation gets its own instance
export class ListStateService {
  private storageKey = '';
  private readonly subject = new BehaviorSubject<ListState>({});
  readonly state$: Observable<ListState> = this.subject.asObservable();

  init(key: string, defaults?: ListState): void {
    this.storageKey = 'ls.' + key;
    const hydrated = this.read() ?? defaults ?? {};
    this.subject.next(hydrated);
  }

  snapshot(): ListState {
    return this.subject.getValue();
  }

  patchFilter(filter: any): void {
    this.commit({ ...this.snapshot(), filter });
  }

  patchSort(sort: any[]): void {
    this.commit({ ...this.snapshot(), sort });
  }

  patchPagination(pagination: ListState['pagination']): void {
    this.commit({ ...this.snapshot(), pagination });
  }

  reset(): void {
    // One call per area: a single try/catch around the pair meant a refused session removal
    // silently skipped the local one, leaving the durable copy to seed the next tab with the
    // state the user just reset (see the "Browser storage" rule in CLAUDE.md).
    removeStorage('session', this.storageKey);
    removeStorage('local', this.storageKey);
    this.subject.next({});
  }

  private commit(next: ListState): void {
    this.write(next);
    this.subject.next(next);
  }

  // hydrate: sessionStorage ?? localStorage ?? null
  //
  // The areas are named, never passed: reading `window.sessionStorage` itself throws SecurityError
  // in a sandboxed iframe or with "block all cookies", so an area resolved at the call site would
  // raise before any guard here could catch it -- and this runs from init(), i.e. list construction.
  private read(): ListState | null {
    const raw = readStorage('session', this.storageKey) ?? readStorage('local', this.storageKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as ListState;
    } catch {
      return null;
    }
  }

  // write-through to BOTH stores, one guarded call each -- a refused session write must not cost
  // the durable localStorage copy, which is what seeds a fresh tab.
  private write(state: ListState): void {
    writeStorageJson('session', this.storageKey, state);
    writeStorageJson('local', this.storageKey, state);
  }
}
